import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { GitCompare, X, Plus } from "lucide-react";
import { useData } from "../lib/data-context";
import { fmtDec, fmtInt, fmtMWh, viabilidadColor } from "../lib/api";
import Semaforo from "../components/Semaforo";

export default function Comparador() {
  const { lineas, loading } = useData();
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");

  const candidates = useMemo(() => {
    if (!lineas) return [];
    const q = search.toLowerCase();
    return lineas
      .filter((l) => !selected.includes(l.codigo_linea))
      .filter(
        (l) =>
          !q ||
          l.numero_linea.toLowerCase().includes(q) ||
          l.denominacion.toLowerCase().includes(q) ||
          l.codigo_linea.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [lineas, selected, search]);

  const selData = useMemo(
    () => (lineas || []).filter((l) => selected.includes(l.codigo_linea)),
    [lineas, selected]
  );

  const chartData = selData.map((l) => ({
    name: l.numero_linea,
    base: l.consumo_kwh_km_base,
    conservador: l.consumo_kwh_km_conservador,
    anual: Math.round(l.consumo_anual_estimado_kwh / 1000),
    vehkm: Math.round(l.veh_km_anuales / 1000),
  }));

  const ref = selData[0];

  if (loading) return <div className="text-gray-500">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="comparador-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Comparador de líneas</div>
        <h1 className="section-title mt-1">Comparativa multi-línea</h1>
        <p className="section-sub">Selecciona dos o más líneas para comparar consumo, viabilidad y operación.</p>
      </div>

      {/* Selected */}
      <div className="crtm-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <GitCompare size={16} /> Líneas seleccionadas ({selected.length})
          </h3>
          {selected.length > 0 && (
            <button
              onClick={() => setSelected([])}
              className="text-xs text-gray-500 hover:text-gray-900"
              data-testid="btn-clear-comparador"
            >
              Vaciar selección
            </button>
          )}
        </div>
        {selected.length === 0 && (
          <p className="text-sm text-gray-500">
            Aún no has seleccionado líneas. Usa el buscador inferior.
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          {selData.map((l) => (
            <span
              key={l.codigo_linea}
              className="inline-flex items-center gap-1.5 bg-[#14532D] text-white text-xs font-semibold px-3 py-1.5 rounded-full"
              data-testid={`chip-${l.codigo_linea}`}
            >
              {l.lote} · {l.numero_linea}
              <button
                onClick={() => setSelected(selected.filter((c) => c !== l.codigo_linea))}
                className="ml-1 hover:text-gray-300"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Picker */}
      <div className="crtm-card p-5">
        <h3 className="font-heading font-semibold text-[15px] mb-3">Añadir línea</h3>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por número, denominación o código…"
          data-testid="comparador-search"
          className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-gray-900"
        />
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          {candidates.map((l) => (
            <button
              key={l.codigo_linea}
              onClick={() => {
                if (selected.length < 6) setSelected([...selected, l.codigo_linea]);
              }}
              data-testid={`add-${l.codigo_linea}`}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50 text-left"
            >
              <div>
                <div className="font-semibold text-sm">
                  {l.lote} · {l.numero_linea}
                </div>
                <div className="text-xs text-gray-500 truncate max-w-[260px]">{l.denominacion}</div>
              </div>
              <Plus size={14} className="text-gray-500" />
            </button>
          ))}
          {candidates.length === 0 && (
            <p className="text-xs text-gray-400 col-span-2">Sin coincidencias.</p>
          )}
        </div>
      </div>

      {selData.length >= 2 && (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Factor kWh/km · base vs conservador">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#4B5563" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="base" name="kWh/km base" fill="#14532D" radius={[4, 4, 0, 0]} maxBarSize={30} />
                  <Bar dataKey="conservador" name="kWh/km cons." fill="#991B1B" radius={[4, 4, 0, 0]} maxBarSize={30} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Consumo anual (MWh)">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#4B5563" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v} MWh`} />
                  <Bar dataKey="anual" name="Consumo anual" fill="#0F1A14" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Summary table */}
          <div className="crtm-card overflow-x-auto">
            <table className="crtm-table">
              <thead>
                <tr>
                  <th>Línea</th>
                  <th>Lote</th>
                  <th>Tipología</th>
                  <th>kWh/km base</th>
                  <th>kWh/km cons.</th>
                  <th>Veh-km</th>
                  <th>Consumo anual</th>
                  <th>Δ vs línea ref</th>
                  <th>Viabilidad</th>
                </tr>
              </thead>
              <tbody>
                {selData.map((l) => {
                  const delta = ref
                    ? ((l.consumo_kwh_km_base - ref.consumo_kwh_km_base) / ref.consumo_kwh_km_base) * 100
                    : 0;
                  return (
                    <tr key={l.codigo_linea}>
                      <td>
                        <div className="font-semibold">{l.numero_linea}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[180px]">{l.denominacion}</div>
                      </td>
                      <td><span className="pill neutral">{l.lote}</span></td>
                      <td className="text-xs">{l.tipologia_vehiculo}</td>
                      <td className="font-mono font-semibold">{fmtDec(l.consumo_kwh_km_base, 2)}</td>
                      <td className="font-mono">{fmtDec(l.consumo_kwh_km_conservador, 2)}</td>
                      <td className="font-mono">{fmtInt(l.veh_km_anuales)}</td>
                      <td className="font-mono">{fmtMWh(l.consumo_anual_estimado_kwh)}</td>
                      <td className={`font-mono font-semibold ${delta > 0 ? "text-red-700" : delta < 0 ? "text-green-700" : "text-gray-500"}`}>
                        {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${fmtDec(delta, 1)}%`}
                      </td>
                      <td><Semaforo viabilidad={l.viabilidad} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-5 py-3 text-xs text-gray-400 border-t border-gray-100">
              Δ calculado respecto a la primera línea seleccionada.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const ChartCard = ({ title, children }) => (
  <div className="crtm-card p-5">
    <h3 className="font-heading font-semibold text-[15px] mb-3">{title}</h3>
    {children}
  </div>
);

const tooltipStyle = {
  background: "white",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  fontSize: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};
